import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { Megaphone, Sparkles, Trash2, Upload } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { AdVariantCard } from "@/components/studio/AdVariantCard"
import { AssetCard } from "@/components/studio/AssetCard"
import { StatusBadge } from "@/components/studio/StatusBadge"
import {
  MEDIA_TYPES,
  type MediaType,
  type StudioAdBatch,
  type StudioAsset,
  type StudioCapabilities,
  type StudioSettings,
  type StudioSuggestion,
} from "@/components/studio/types"
import type { PageProps } from "@/types/inertia"

type Props = PageProps & {
  article: { id: number; title_en: string; status: string; slug: string; excerpt_en: string | null }
  assets: StudioAsset[]
  ad_batches: StudioAdBatch[]
  suggestions: StudioSuggestion[]
  settings: StudioSettings
  ai_enabled: boolean
  media_enabled: boolean
  meta_enabled: boolean
  instagram_enabled: boolean
  ads_enabled: boolean
}

const BUSY_ASSET_STATUSES = new Set(["queued", "generating"])
const BUSY_BATCH_STATUSES = new Set(["generating", "uploading"])
const BUSY_VARIANT_STATUSES = new Set(["draft", "generating_image", "uploading"])

function UploadBatchDialog({ batch, adsEnabled }: { batch: StudioAdBatch; adsEnabled: boolean }) {
  const [open, setOpen] = React.useState(false)

  function upload() {
    setOpen(false)
    router.patch(`/admin/studio/ad-batches/${batch.id}/upload`, {}, { preserveScroll: true })
  }

  if (!adsEnabled) {
    return (
      <Button type="button" size="sm" disabled title="Set META_ACCESS_TOKEN, META_AD_ACCOUNT_ID and META_PAGE_ID to enable ad uploads">
        <Upload className="mr-1.5 h-3.5 w-3.5" />
        Upload to Meta (paused)
      </Button>
    )
  }

  return (
    <>
      <Button type="button" size="sm" onClick={() => setOpen(true)}>
        <Upload className="mr-1.5 h-3.5 w-3.5" />
        Upload to Meta (paused)
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload this batch to Meta?</DialogTitle>
            <DialogDescription>
              Every campaign, ad set, and ad is created PAUSED — nothing spends money here. Set the
              budget and audience and activate the ads in Ads Manager when you&apos;re ready.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={upload}>
              Upload paused ads
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function AdBatchSection({ batch, adsEnabled }: { batch: StudioAdBatch; adsEnabled: boolean }) {
  function destroy() {
    router.delete(`/admin/studio/ad-batches/${batch.id}`, { preserveScroll: true })
  }

  return (
    <div className="rounded-lg border border-hairline bg-surface">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline px-5 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3>Batch of {batch.variant_count}</h3>
          <StatusBadge status={batch.status} />
          <span className="text-xs text-ink-muted">
            {new Date(batch.created_at).toLocaleDateString("en-GB")}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {batch.status === "ready" && <UploadBatchDialog batch={batch} adsEnabled={adsEnabled} />}
          {(batch.status === "ready" || batch.status === "failed") && (
            <Button type="button" size="sm" variant="ghost" onClick={destroy}>
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Remove
            </Button>
          )}
        </div>
      </div>
      {batch.from_suggestion && (
        <p className="border-b border-hairline px-5 py-2 text-xs text-ink-muted">
          From suggestion: {batch.from_suggestion}
        </p>
      )}
      {batch.error_message && (
        <p className="border-b border-hairline px-5 py-2 text-sm text-ink-muted">{batch.error_message}</p>
      )}
      {batch.status === "generating" && batch.variants.length === 0 ? (
        <p className="px-5 py-6 text-center text-sm text-ink-muted">
          Writing the angles — variants appear here as they generate.
        </p>
      ) : (
        <div className="grid gap-4 p-5 sm:grid-cols-2 xl:grid-cols-3">
          {batch.variants.map((variant) => (
            <AdVariantCard key={variant.id} variant={variant} />
          ))}
        </div>
      )}
    </div>
  )
}

function SuggestionCard({ suggestion }: { suggestion: StudioSuggestion }) {
  function act(action: "accept" | "dismiss") {
    router.patch(`/admin/studio/ad-suggestions/${suggestion.id}/${action}`, {}, { preserveScroll: true })
  }

  return (
    <li className="px-5 py-4">
      <p className="text-sm text-ink-body">{suggestion.summary}</p>
      {suggestion.rationale && <p className="mt-1 text-sm text-ink-muted">{suggestion.rationale}</p>}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {suggestion.proposed_angles.map((angle) => (
          <Badge key={angle.angle} tone="neutral" title={angle.brief}>
            {angle.angle.replace(/_/g, " ")}
          </Badge>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button type="button" size="sm" onClick={() => act("accept")}>
          <Sparkles className="mr-1.5 h-3.5 w-3.5" />
          Accept — generate new batch
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => act("dismiss")}>
          Dismiss
        </Button>
      </div>
    </li>
  )
}

export default function StudioArticle() {
  const { props } = usePage<Props>()
  const {
    article,
    assets,
    ad_batches,
    suggestions,
    settings,
    flash,
    ai_enabled,
    media_enabled,
    meta_enabled,
    instagram_enabled,
    ads_enabled,
  } = props
  const capabilities: StudioCapabilities = {
    ai_enabled,
    media_enabled,
    meta_enabled,
    instagram_enabled,
    ads_enabled,
  }

  // Newest asset per media type is "current"; older ones form the library.
  const currentByType = new Map<MediaType, StudioAsset>()
  const library: StudioAsset[] = []
  for (const asset of assets) {
    if (currentByType.has(asset.media_type)) {
      library.push(asset)
    } else {
      currentByType.set(asset.media_type, asset)
    }
  }

  // Poll with an Inertia partial reload while anything is generating or
  // uploading — no JSON endpoints, per the Inertia response rules.
  const busy =
    assets.some((a) => BUSY_ASSET_STATUSES.has(a.status)) ||
    assets.some((a) => a.status === "awaiting_approval" && !a.prompt) ||
    ad_batches.some(
      (b) =>
        BUSY_BATCH_STATUSES.has(b.status) ||
        b.variants.some((v) => BUSY_VARIANT_STATUSES.has(v.status)),
    )
  React.useEffect(() => {
    if (!busy) return
    const interval = window.setInterval(() => {
      router.reload({ only: ["assets", "ad_batches"] })
    }, 5000)
    return () => window.clearInterval(interval)
  }, [busy])

  function generateBatch() {
    router.post(
      "/admin/studio/ad-batches",
      { article_id: article.id },
      { preserveScroll: true },
    )
  }

  const canGenerateBatch = ai_enabled && media_enabled
  const batchBusy = ad_batches.some((b) => b.status === "generating")

  return (
    <AdminShell>
      <Head title={`Studio — ${article.title_en}`}>
        <meta
          name="description"
          content={`Derived media for "${article.title_en}": social posts, posters, ads, and video.`}
        />
        <meta property="og:title" content={`Studio — ${article.title_en}`} />
        <meta
          property="og:description"
          content={`Derived media for "${article.title_en}": social posts, posters, ads, and video.`}
        />
      </Head>
      <PageHeader
        title={article.title_en}
        description={
          <>
            {article.status === "published" ? "Published article" : "Draft article"} ·{" "}
            <Link href="/admin/studio">Back to Content Studio</Link>
          </>
        }
        actions={
          <Button type="button" variant="secondary" asChild>
            <Link href={`/admin/articles/${article.id}/edit`} className="no-underline">
              Edit article
            </Link>
          </Button>
        }
      />

      {flash.notice && (
        <div className="mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {flash.notice}
        </div>
      )}
      {flash.alert && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {flash.alert}
        </div>
      )}

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {MEDIA_TYPES.map((type) => (
          <AssetCard
            key={type}
            mediaType={type}
            asset={currentByType.get(type) ?? null}
            articleId={article.id}
            capabilities={capabilities}
            autoPublish={settings.auto_publish[type]}
          />
        ))}
      </div>

      {/* Facebook ads agent */}
      <div className="mt-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2>Facebook ads</h2>
            <p className="mt-0.5 text-sm text-ink-muted">
              Batches of {settings.ad_batch_size} ad variants, each taking a different angle.
              Uploads are always paused — budget, audience, and activation stay in Ads Manager.
            </p>
          </div>
          <Button
            type="button"
            onClick={generateBatch}
            disabled={!canGenerateBatch || batchBusy}
            title={
              canGenerateBatch
                ? undefined
                : !ai_enabled
                  ? "Set ANTHROPIC_API_KEY to enable AI drafting"
                  : "Set FAL_KEY to enable image generation"
            }
          >
            <Megaphone className="mr-1.5 h-4 w-4" />
            Generate ad batch ({settings.ad_batch_size} variants)
          </Button>
        </div>

        {suggestions.length > 0 && (
          <div className="mt-4 rounded-lg border border-hairline bg-surface">
            <div className="border-b border-hairline px-5 py-3">
              <h3>Suggestions from the weekly ads agent</h3>
            </div>
            <ul className="divide-y divide-hairline">
              {suggestions.map((s) => (
                <SuggestionCard key={s.id} suggestion={s} />
              ))}
            </ul>
          </div>
        )}

        {ad_batches.length === 0 ? (
          <p className="mt-4 rounded-lg border border-hairline bg-surface px-5 py-6 text-center text-sm text-ink-muted">
            No ad batches yet.
          </p>
        ) : (
          <div className="mt-4 flex flex-col gap-4">
            {ad_batches.map((batch) => (
              <AdBatchSection key={batch.id} batch={batch} adsEnabled={ads_enabled} />
            ))}
          </div>
        )}
      </div>

      {library.length > 0 && (
        <div className="mt-8 rounded-lg border border-hairline bg-surface">
          <div className="border-b border-hairline px-5 py-3">
            <h2>Earlier versions</h2>
            <p className="mt-0.5 text-sm text-ink-muted">
              Previous generations of this article&apos;s media.
            </p>
          </div>
          <ul className="divide-y divide-hairline">
            {library.map((asset) => (
              <li key={asset.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                <div className="flex items-center gap-2">
                  <StatusBadge status={asset.status} />
                  <span className="text-sm text-ink-body">
                    {asset.media_type.replace(/_/g, " ")} ·{" "}
                    {new Date(asset.created_at).toLocaleDateString("en-GB")}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {asset.download_url && (
                    <Button type="button" size="sm" variant="ghost" asChild>
                      <a href={asset.download_url} download className="no-underline">
                        Download
                      </a>
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => router.delete(`/admin/studio/assets/${asset.id}`, { preserveScroll: true })}
                  >
                    Remove
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </AdminShell>
  )
}
